<template>
  <q-table
    bordered
    square
    dense
    flat
    row-key="id"
    :rows="rows"
    :columns="columns"
    :rows-per-page-options="[0]"
    :hide-bottom="rows.length > 0"
    :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    :class="printingMode ? '' : 'sticky-headers'"
  >
    <template #header-cell-items>
      <th v-for="x in itemColumns" :key="x.label" style="width: 138px" scope="col">
        {{ x.label }}
      </th>
    </template>
    <template #body-cell-concessions="props">
      <q-td :props="props" style="min-width: 400px; white-space: normal; max-width: 400px">
        {{ props.value }}
      </q-td>
    </template>
    <template #body-cell-isActive="props">
      <q-td :props="props">
        <q-icon v-if="props.value" name="check_circle" color="green" size="xs" />
        <q-icon v-else name="cancel" color="grey-6" size="xs" />
      </q-td>
    </template>
    <template #body-cell-items="props">
      <q-td :props="props" class="no-padding" :colspan="itemColumns.length">
        <q-markup-table flat wrap-cells dense square class="bg-transparent">
          <tbody>
            <tr v-for="row in props.value" :key="row.id">
              <td
                v-for="col in itemColumns"
                :key="col.label"
                :style="`width: ${(1 / itemColumns.length) * 100}%`"
              >
                <div v-for="(val, i) in [col.field(row)]" :key="i">
                  <div v-if="val === true" class="text-center">
                    <q-icon name="check_circle" color="grey" size="xs" />
                  </div>
                  <div v-else-if="val === false" class="text-center"></div>
                  <div v-else-if="Array.isArray(val)" class="row">
                    <div v-for="v in col.format(val)" :key="v">
                      <q-chip :key="v" dense style="font-size: small">
                        {{ v }}
                      </q-chip>
                    </div>
                  </div>
                  <div v-else class="text-center">
                    {{ val }}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </q-markup-table>
      </q-td>
    </template>
    <template #no-data>
      <div class="q-pa-md q-mx-auto">
        No concessions found for the filtered property and date range
      </div>
    </template>
  </q-table>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { QTableColumn } from 'quasar'
import { formatDate, parseISO, differenceInDays } from 'date-fns'

import type { Concessions, ConcessionsItem, PropertyDetailsResponse } from 'common'
import { formatMoney, formatPercent } from '@/utils'

const quote = (val: string | number | string[] | null | undefined) => {
  if (typeof val === 'string') {
    return `"${val}"`
  }
  return val || ''
}

export default {
  name: 'ConcessionsTable',
  props: {
    concessions: {
      type: Array<{ concession: Concessions; prop: PropertyDetailsResponse }>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date } | null>,
      default: null
    },
    unitLevel: {
      type: Boolean,
      default: false
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    rows() {
      const fromStr = this.dateRange?.from ? formatDate(this.dateRange.from, 'yyyy-MM-dd') : null
      const toStr = this.dateRange?.to ? formatDate(this.dateRange.to, 'yyyy-MM-dd') : null
      return this.concessions
        .filter(({ concession }) => {
          if (fromStr && toStr) {
            return !(concession.to_date < fromStr || concession.from_date > toStr)
          }
          return true
        })
        .map(({ concession, prop }) => {
          return {
            label: prop.building_name || prop.street_address,
            ...concession,
            from: concession.from_date
              ? formatDate(parseISO(concession.from_date), 'M/d/yyyy')
              : '',
            to: concession.to_date ? formatDate(parseISO(concession.to_date), 'M/d/yyyy') : '',
            isActive: concession.to_date >= prop.created_on
          }
        })
    },
    columns() {
      return [
        ...(!this.unitLevel
          ? [{ field: 'label', align: 'left', classes: 'constrained-cell' }]
          : []),
        {
          name: 'concessions',
          label: 'Concession',
          field: 'concessions',
          align: 'left'
        },
        ...(!this.unitLevel
          ? [
              {
                name: 'isActive',
                label: 'Active',
                field: 'isActive',
                align: 'center'
              }
            ]
          : []),
        { label: 'From', field: 'from', align: 'left' },
        { label: 'To', field: 'to', align: 'left' },
        {
          label: '# Days',
          field: (row) => differenceInDays(row.to, row.from) + 1,
          align: 'center'
        },
        { name: 'items', label: 'Items', field: 'items' }
      ] as QTableColumn[]
    },
    itemColumns() {
      return [
        {
          label: 'Beds',
          field: (row: ConcessionsItem) => {
            return row.condition_bedrooms
          },
          format: (val: number[]) => {
            return val.length ? val : ['All']
          }
        },
        {
          label: 'Unit Names',
          field: (row: ConcessionsItem) => {
            return row.condition_unit_names
          },
          format: (val: number[]) => {
            return val.length ? val : ['All']
          }
        },
        {
          label: 'Floorplans',
          field: (row: ConcessionsItem) => {
            return row.condition_floorplans
          },
          format: (val: number[]) => {
            return val.length ? val : ['All']
          }
        },
        {
          label: 'Lease Terms',
          field: (row: ConcessionsItem) => {
            return row.condition_lease_term_months
          },
          format: (val: number[]) => {
            return val.length ? val : ['All']
          }
        },
        {
          label: 'Deadline',
          field: (row: ConcessionsItem) => {
            return row.condition_deadline
              ? formatDate(parseISO(row.condition_deadline), 'M/d/yyyy')
              : ''
          },
          format: (val: string) => val
        },
        {
          label: 'Specific Unit',
          field: (row: ConcessionsItem) => {
            return row.condition_specific_unit
          },
          format: (val: boolean | null) => val
        },
        {
          label: 'Free Months',
          field: (row: ConcessionsItem) => {
            return row.free_months_count
          },
          format: (val: number | null) => val
        },
        {
          label: 'Free Weeks',
          field: (row: ConcessionsItem) => {
            return row.free_weeks_count
          },
          format: (val: number | null) => val
        },
        {
          label: 'Free Until',
          field: (row: ConcessionsItem) => {
            return row.free_months_until
              ? formatDate(parseISO(row.free_months_until), 'M/d/yyyy')
              : ''
          },
          format: (val: string) => val
        },
        {
          label: 'One Time Off',
          field: (row: ConcessionsItem) => {
            const values: string[] = []
            if (row.one_time_dollars_off_amount) {
              values.push(formatMoney(row.one_time_dollars_off_amount))
            }
            if (row.one_time_dollars_off_percentage) {
              values.push(formatPercent(row.one_time_dollars_off_percentage / 100))
            }
            return values
          },
          format: (val: string[]) => val
        },
        {
          label: 'Recurring Off',
          field: (row: ConcessionsItem) => {
            const values: string[] = []
            if (row.recurring_dollars_off_amount) {
              values.push(formatMoney(row.recurring_dollars_off_amount))
            }
            if (row.recurring_dollars_off_percentage) {
              values.push(formatPercent(row.recurring_dollars_off_percentage / 100))
            }
            return values
          },
          format: (val: string[]) => val
        },
        {
          label: 'Waived Fees',
          field: (row: ConcessionsItem) => {
            const waivedFees = []

            if (row.waived_application_fee) {
              waivedFees.push('App Fee')
            }
            if (row.waived_security_deposit) {
              waivedFees.push('Security Deposit')
            }
            if (row.waived_administrative_fee) {
              waivedFees.push('Admin Fee')
            }
            if (row.waived_move_in_fee) {
              waivedFees.push('Move-in Fee')
            }
            return waivedFees
          },
          format: (val: string[]) => val
        },
        {
          label: 'Cheaper Fees',
          field: (row: ConcessionsItem) => {
            const cheaperFees = []

            if (row.cheaper_application_fee) {
              cheaperFees.push('App Fee')
            }
            if (row.cheaper_security_deposit) {
              cheaperFees.push('Security Deposit')
            }
            if (row.cheaper_administrative_fee) {
              cheaperFees.push('Admin Fee')
            }
            if (row.cheaper_move_in_fee) {
              cheaperFees.push('Move-in Fee')
            }
            if (row.cheaper_rent) {
              cheaperFees.push('Rent')
            }
            return cheaperFees
          },
          format: (val: string[]) => val
        }
      ].filter((col) => {
        return this.rows.some((row) =>
          row.items.some((item) => {
            const val = col.field(item)
            return Array.isArray(val) ? val.length > 0 : Boolean(val)
          })
        )
      })
    }
  },
  methods: {
    exportCSV() {
      // takes rows and columns and returns a CSV
      // compute headers as headers + itemColumns
      const headersLabels = this.columns
        .filter((col) => col.field !== 'items')
        .map((col) => col.label)

      const itemHeadersLabels = this.itemColumns.map((col) => col.label)

      const csv = [
        headersLabels.concat(itemHeadersLabels).join(','),
        ...this.rows.map((row) => {
          const values = this.columns
            .filter((col) => col.field !== 'items')
            .map((col) => {
              const val = quote(row[col.field])
              return Array.isArray(val) ? quote(val.join(', ')) : val
            })

          const itemValues = this.itemColumns.map((col) => {
            const relevantItem = row.items.find((item) => col.field(item))
            if (!relevantItem) {
              return ''
            }
            return Array.isArray(col.field(relevantItem))
              ? quote(col.field(relevantItem).join(', '))
              : quote(col.field(relevantItem))
          })

          return values.concat(itemValues).join(',')
        })
      ].join('\n')

      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `hello-data-concessions-${formatDate(new Date(), 'yyyy-MM-dd')}.csv`
      a.click()
    }
  }
}
</script>
<style scoped lang="scss">
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sticky-headers {
  max-height: 400px;
  overflow: auto;

  ::v-deep(.q-table__top),
  ::v-deep(.q-table__bottom),
  ::v-deep(thead tr th) {
    position: sticky;
    z-index: 1;
  }

  ::v-deep(.q-table__top) {
    top: 0;
  }

  ::v-deep(thead tr th) {
    top: 0;
  }

  ::v-deep(tbody) {
    scroll-margin-top: 48px;
  }
}
</style>
